'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { useOrganizationProfile } from '@/hooks/useOrganizationProfile';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useToast } from '@/hooks/use-toast';
import { useIsOrganizationOwner } from '@/hooks/useIsOrganizationOwner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Building2, 
  Users, 
  Globe,
  Camera,
  Plus,
  X,
  MapPin,
  DollarSign,
  Settings,
  Shield,
  Eye,
  EyeOff,
  Edit
} from 'lucide-react';

export default function OrganizationProfilePage() {
  const { profile, completion, organizationName, loading, createProfile, updateProfile, togglePublicVisibility } = useOrganizationProfile();
  const { organizations } = useOrganizations();
  const { toast } = useToast();
  const isOwner = useIsOrganizationOwner();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    industry: '',
    company_size: '',
    founded_year: new Date().getFullYear(),
    headquarters_location: '',
    website_url: '',
    description: '',
    logo_url: '',
    annual_revenue_range: '',
    number_of_locations: 1,
    parent_company: '',
    stock_symbol: '',
    duns_number: '',
    annual_procurement_volume: '',
    preferred_payment_terms: '',
    public_description: '',
    contact_email: '',
    is_public_profile: false,
    typical_project_types: [] as string[],
    social_media_links: {},
    vendor_requirements: {}
  });

  React.useEffect(() => {
    // Only update form data if we're not currently editing or submitting
    if (profile && !isEditing && !isSubmitting) {
      setFormData({
        industry: profile.industry || '',
        company_size: profile.company_size || '',
        founded_year: profile.founded_year || new Date().getFullYear(),
        headquarters_location: profile.headquarters_location || '',
        website_url: profile.website_url || '',
        description: profile.description || '',
        logo_url: profile.logo_url || '',
        annual_revenue_range: profile.annual_revenue_range || '',
        number_of_locations: profile.number_of_locations || 1,
        parent_company: profile.parent_company || '',
        stock_symbol: profile.stock_symbol || '',
        duns_number: profile.duns_number || '',
        annual_procurement_volume: profile.annual_procurement_volume || '',
        preferred_payment_terms: profile.preferred_payment_terms || '',
        public_description: profile.public_description || '',
        contact_email: profile.contact_email || '',
        is_public_profile: profile.is_public_profile || false,
        typical_project_types: Array.isArray(profile.typical_project_types) ? profile.typical_project_types : [],
        social_media_links: profile.social_media_links || {},
        vendor_requirements: profile.vendor_requirements || {}
      });
    }
  }, [profile, isEditing, isSubmitting]);

  const handleSave = async () => {
    if (!isOwner) {
      toast({
        title: "Permission Denied",
        description: "Only the organization owner can edit the profile",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (profile) {
        await updateProfile(formData);
      } else {
        await createProfile(formData);
      }
      
      // Keep form protected briefly after save
      setTimeout(() => {
        setIsEditing(false);
      }, 200);
      
      toast({
        title: "Success",
        description: "Organization profile updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update organization profile",
        variant: "destructive",
      });
    } finally {
      // Clear submission flag after cooldown
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Organization Profile</h1>
            {organizationName && (
              <div className="flex items-center gap-2 mt-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <p className="text-xl font-semibold text-muted-foreground">
                  {organizationName}
                </p>
              </div>
            )}
            <p className="text-muted-foreground mt-1">
              {isOwner 
                ? "Manage your organization's public and internal profile information"
                : "View your organization's profile information"}
            </p>
          </div>
          {isOwner && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={togglePublicVisibility}
                disabled={loading}
              >
                {formData.is_public_profile ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Make Private
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Make Public
                  </>
                )}
              </Button>
              <Button
                onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                disabled={loading}
              >
                {isEditing ? 'Save Changes' : 'Edit Profile'}
              </Button>
            </div>
          )}
        </div>

        {/* Read-only Alert for Non-Owners */}
        {!isOwner && (
          <Alert>
            <AlertDescription>
              Only the organization owner can edit the company profile. You are viewing this information in read-only mode.
            </AlertDescription>
          </Alert>
        )}

        {/* Profile Completion */}
        {completion && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Profile Completion</CardTitle>
                <Badge variant={completion.overall_percentage > 80 ? "default" : "secondary"}>
                  {completion.overall_percentage}% Complete
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Progress value={completion.overall_percentage} className="w-full mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${completion.basic_info_complete ? 'bg-success' : 'bg-muted'}`} />
                  <span>Basic Information</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${completion.business_info_complete ? 'bg-success' : 'bg-muted'}`} />
                  <span>Business Information</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${completion.member_info_complete ? 'bg-success' : 'bg-muted'}`} />
                  <span>Member Information</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Content */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="business">Business Info</TabsTrigger>
            <TabsTrigger value="procurement">Procurement</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="public">Public Profile</TabsTrigger>
          </TabsList>

          {/* Company Overview */}
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Company Overview
                </CardTitle>
                <CardDescription>
                  Basic information about your organization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo */}
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20 rounded-lg">
                    <AvatarImage src={formData.logo_url} />
                    <AvatarFallback className="rounded-lg">
                      <Building2 className="h-8 w-8" />
                    </AvatarFallback>
                  </Avatar>
                  {isEditing && (
                    <div className="space-y-2">
                      <Button variant="outline" size="sm">
                        <Camera className="h-4 w-4 mr-2" />
                        Upload Logo
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        PNG, JPG up to 5MB
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Select
                      value={formData.industry}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, industry: value }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="technology">Technology</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="construction">Construction</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_size">Company Size</Label>
                    <Select
                      value={formData.company_size}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, company_size: value }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select company size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10 employees</SelectItem>
                        <SelectItem value="11-50">11-50 employees</SelectItem>
                        <SelectItem value="51-200">51-200 employees</SelectItem>
                        <SelectItem value="201-1000">201-1000 employees</SelectItem>
                        <SelectItem value="1000+">1000+ employees</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="founded_year">Founded Year</Label>
                    <Input
                      id="founded_year"
                      type="number"
                      value={formData.founded_year}
                      onChange={(e) => setFormData(prev => ({ ...prev, founded_year: parseInt(e.target.value) }))}
                      disabled={!isEditing}
                      min="1800"
                      max={new Date().getFullYear()}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="headquarters_location">Headquarters Location</Label>
                    <Input
                      id="headquarters_location"
                      value={formData.headquarters_location}
                      onChange={(e) => setFormData(prev => ({ ...prev, headquarters_location: e.target.value }))}
                      disabled={!isEditing}
                      placeholder="City, State, Country"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="website_url">Website</Label>
                    <Input
                      id="website_url"
                      value={formData.website_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                      disabled={!isEditing}
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Company Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    disabled={!isEditing}
                    placeholder="Describe your organization's mission, values, and what you do..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Information */}
          <TabsContent value="business">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Business Information
                </CardTitle>
                <CardDescription>
                  Financial and operational details about your organization
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="annual_revenue_range">Annual Revenue Range</Label>
                    <Select
                      value={formData.annual_revenue_range}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, annual_revenue_range: value }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select revenue range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under_1m">Under $1M</SelectItem>
                        <SelectItem value="1m_10m">$1M - $10M</SelectItem>
                        <SelectItem value="10m_50m">$10M - $50M</SelectItem>
                        <SelectItem value="50m_100m">$50M - $100M</SelectItem>
                        <SelectItem value="100m_500m">$100M - $500M</SelectItem>
                        <SelectItem value="500m_1b">$500M - $1B</SelectItem>
                        <SelectItem value="over_1b">Over $1B</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="number_of_locations">Number of Locations</Label>
                    <Input
                      id="number_of_locations"
                      type="number"
                      value={formData.number_of_locations}
                      onChange={(e) => setFormData(prev => ({ ...prev, number_of_locations: parseInt(e.target.value) }))}
                      disabled={!isEditing}
                      min="1"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="parent_company">Parent Company</Label>
                    <Input
                      id="parent_company"
                      value={formData.parent_company}
                      onChange={(e) => setFormData(prev => ({ ...prev, parent_company: e.target.value }))}
                      disabled={!isEditing}
                      placeholder="If applicable"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="stock_symbol">Stock Symbol</Label>
                    <Input
                      id="stock_symbol"
                      value={formData.stock_symbol}
                      onChange={(e) => setFormData(prev => ({ ...prev, stock_symbol: e.target.value }))}
                      disabled={!isEditing}
                      placeholder="If publicly traded"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duns_number">DUNS Number</Label>
                    <Input
                      id="duns_number"
                      value={formData.duns_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, duns_number: e.target.value }))}
                      disabled={!isEditing}
                      placeholder="9-digit identifier"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Procurement Profile */}
          <TabsContent value="procurement">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Procurement Profile
                </CardTitle>
                <CardDescription>
                  Your organization's procurement preferences and requirements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="annual_procurement_volume">Annual Procurement Volume</Label>
                    <Select
                      value={formData.annual_procurement_volume}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, annual_procurement_volume: value }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select volume range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under_100k">Under $100K</SelectItem>
                        <SelectItem value="100k_500k">$100K - $500K</SelectItem>
                        <SelectItem value="500k_1m">$500K - $1M</SelectItem>
                        <SelectItem value="1m_5m">$1M - $5M</SelectItem>
                        <SelectItem value="5m_10m">$5M - $10M</SelectItem>
                        <SelectItem value="over_10m">Over $10M</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferred_payment_terms">Preferred Payment Terms</Label>
                    <Select
                      value={formData.preferred_payment_terms}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_payment_terms: value }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment terms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="net_15">Net 15</SelectItem>
                        <SelectItem value="net_30">Net 30</SelectItem>
                        <SelectItem value="net_45">Net 45</SelectItem>
                        <SelectItem value="net_60">Net 60</SelectItem>
                        <SelectItem value="upon_completion">Upon Completion</SelectItem>
                        <SelectItem value="milestone_based">Milestone Based</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Typical Project Types</Label>
                    {isEditing && (
                      <Button variant="outline" size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Type
                      </Button>
                    )}
                  </div>
                  
                  {formData.typical_project_types.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.typical_project_types.map((type, index) => (
                        <Badge key={index} variant="secondary" className="px-3 py-1">
                          {type}
                          {isEditing && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto p-0 ml-2"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  typical_project_types: prev.typical_project_types.filter((_, i) => i !== index)
                                }));
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No project types added yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Organization Members */}
          <TabsContent value="members">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Organization Members
                </CardTitle>
                <CardDescription>
                  Manage your organization's team members and their roles
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Organization Members</h3>
                  <p className="text-muted-foreground mb-4">
                    Member management features coming soon
                  </p>
                  <Button variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Invite Members
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Public Profile */}
          <TabsContent value="public">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Public Company Profile
                </CardTitle>
                <CardDescription>
                  Information visible to vendors and other platform users
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-base">Public Profile Visibility</Label>
                    <p className="text-sm text-muted-foreground">
                      Make your organization discoverable by vendors
                    </p>
                  </div>
                  <Switch 
                    checked={formData.is_public_profile}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public_profile: checked }))}
                    disabled={!isEditing}
                  />
                </div>

                {formData.is_public_profile && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="public_description">Public Description</Label>
                      <Textarea
                        id="public_description"
                        value={formData.public_description}
                        onChange={(e) => setFormData(prev => ({ ...prev, public_description: e.target.value }))}
                        disabled={!isEditing}
                        placeholder="Brief description for public profile..."
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact_email">Public Contact Email</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                        disabled={!isEditing}
                        placeholder="vendor-inquiries@company.com"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}